import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createPageUrl } from '../utils';
import { useAuth } from '../firebase/AuthContext';
import { useLanguage } from '../components/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import PrintConfigModal from '../components/photobooth/PrintConfigModal';
import { Printer, Share2, X as XIcon } from 'lucide-react';
import {
  Coins,
  Image,
  Users,
  Copy,
  Check,
  Gift,
  Download,
  ExternalLink,
  CreditCard,
  Sparkles,
  LogIn,
  Camera,
  Calendar,
  Wand2,
  Frame
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';

export default function Dashboard() {
  const { t, language, isRTL } = useLanguage();
  const { user, loading, fetchGenerations, getAuthToken } = useAuth();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [generations, setGenerations] = useState([]);
  // Opening a past photo used to just follow a link to the raw file, which left
  // no way to reprint or share it — the two things an operator actually wants
  // from a history gallery.
  const [viewing, setViewing] = useState(null);
  const [printing, setPrinting] = useState(null);
  const [loadingGens, setLoadingGens] = useState(true);
  const [myEvents, setMyEvents] = useState([]);
  const [myCustomChars, setMyCustomChars] = useState([]);

  // Load history when user is available
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const list = await fetchGenerations(24);
        setGenerations(list);
      } finally {
        setLoadingGens(false);
      }
    })();
    // Events + custom characters — load from SQLite via API
    (async () => {
      try {
        const token = await getAuthToken();
        if (!token) return;
        const [evRes, ccRes] = await Promise.all([
          fetch('/api/events', { headers: { Authorization: `Bearer ${token}` } }),
          fetch('/api/custom-characters', { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        if (evRes.ok) {
          const j = await evRes.json();
          setMyEvents(j.events || []);
        }
        if (ccRes.ok) {
          const j = await ccRes.json();
          setMyCustomChars(j.characters || []);
        }
      } catch (e) {
        console.warn('Dashboard data fetch failed:', e.message);
      }
    })();
  }, [user, fetchGenerations, getAuthToken]);

  // Redirect to login if not authenticated
  if (!loading && !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-5xl mb-6">🔐</div>
          <h2 className="text-2xl font-bold text-white mb-3">
            {language === 'he' ? 'נדרשת התחברות' : 'Login Required'}
          </h2>
          <p className="text-slate-400 mb-6">
            {language === 'he' ? 'עליך להתחבר כדי לגשת ללוח הבקרה' : 'You need to log in to access the dashboard'}
          </p>
          <Link to="/login">
            <Button className="bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-700 hover:to-pink-700 text-white px-8 py-3 text-lg">
              <LogIn className="w-5 h-5 mr-2" />
              {language === 'he' ? 'התחברות' : 'Login'}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const referralCode = user?.referral_code || generateLocalReferralCode(user?.uid, user?.email);

  const copyReferralCode = async () => {
    try {
      if (!referralCode) {
        toast.error(language === 'he' ? 'קוד הפניה לא זמין כרגע' : 'Referral code is not available yet');
        return;
      }
      await navigator.clipboard.writeText(referralCode);
      setCopied(true);
      toast.success(language === 'he' ? 'הועתק!' : 'Copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  const stats = [
    {
      title: t('yourCredits'),
      value: user?.credits ?? '—',
      icon: Coins,
      color: 'from-amber-500 to-orange-500',
      bgColor: 'from-amber-500/20 to-orange-500/20',
    },
    {
      title: t('totalGenerations'),
      value: user?.total_generations ?? 0,
      icon: Image,
      color: 'from-violet-500 to-purple-500',
      bgColor: 'from-violet-500/20 to-purple-500/20',
    },
    {
      title: language === 'he' ? 'קוד הפניה' : 'Referral Code',
      value: referralCode || '—',
      icon: Users,
      color: 'from-pink-500 to-rose-500',
      bgColor: 'from-pink-500/20 to-rose-500/20',
      isText: true,
    },
  ];

  function generateLocalReferralCode(uid, email) {
    if (!uid) return '';
    const prefix = String(email || '').slice(0, 4).toUpperCase().replace(/[^A-Z0-9]/g, '') || 'USER';
    const suffix = String(uid).slice(-6).toUpperCase();
    return `REF${prefix}${suffix}`;
  }

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-bold text-white mb-2">{t('myDashboard')}</h1>
          <p className="text-slate-400">
            {language === 'he' ? 'ברוך הבא' : 'Welcome'}, {user?.full_name || user?.email}
          </p>
        </motion.div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {stats.map((stat, idx) => (
            <motion.div
              key={stat.title}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
            >
              <Card className="glass-card border-slate-800">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-slate-400 text-sm mb-1">{stat.title}</p>
                      <p className="text-4xl font-bold text-white">{stat.value}</p>
                    </div>
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stat.bgColor} flex items-center justify-center`}>
                      <stat.icon className={`w-6 h-6 bg-gradient-to-br ${stat.color} bg-clip-text text-white`} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Referral Program */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="glass-card border-slate-800 h-full">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <Gift className="w-5 h-5 text-pink-400" />
                  {t('referralProgram')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                {/* Referral CODE */}
                <div className="bg-slate-800/50 rounded-xl p-4">
                  <p className="text-sm text-slate-400 mb-2">{t('yourReferralCode')}</p>
                  <div className="flex items-center gap-3">
                    <code className="flex-1 bg-slate-900 px-4 py-3 rounded-lg text-violet-400 font-mono text-lg truncate">
                      {referralCode || '—'}
                    </code>
                    <Button
                      onClick={copyReferralCode}
                      className="bg-violet-600 hover:bg-violet-700 text-white font-medium"
                    >
                      {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                    </Button>
                  </div>
                </div>

                {/* Shareable LINK */}
                {referralCode && (
                  <div className="bg-slate-800/50 rounded-xl p-4 space-y-3">
                    <p className="text-sm text-slate-400">
                      {language === 'he' ? 'קישור הזמנה לחברים' : 'Invite link for friends'}
                    </p>
                    {(() => {
                      const inviteUrl = `${window.location.origin}/login?ref=${encodeURIComponent(referralCode)}`;
                      const inviteText = language === 'he'
                        ? `🎁 הצטרף ל-AI PhotoBooth וקבל 10 קרדיטים מתנה! השתמש בקוד שלי בהרשמה: ${referralCode}\n${inviteUrl}`
                        : `🎁 Join AI PhotoBooth and get 10 free credits! Use my code at signup: ${referralCode}\n${inviteUrl}`;
                      return (
                        <>
                          <div className="flex items-center gap-2">
                            <code className="flex-1 bg-slate-900 px-3 py-2 rounded-lg text-xs text-slate-300 truncate font-mono">
                              {inviteUrl}
                            </code>
                            <Button
                              size="sm"
                              onClick={() => {
                                navigator.clipboard.writeText(inviteUrl);
                                toast.success(language === 'he' ? 'הקישור הועתק!' : 'Link copied!');
                              }}
                              className="bg-slate-700 hover:bg-slate-600 text-white shrink-0"
                              title={language === 'he' ? 'העתק קישור' : 'Copy link'}
                            >
                              <Copy className="w-4 h-4" />
                            </Button>
                          </div>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                            <a
                              href={`https://wa.me/?text=${encodeURIComponent(inviteText)}`}
                              target="_blank" rel="noopener noreferrer"
                              className="flex flex-col items-center gap-1 p-3 rounded-lg bg-green-500/10 border border-green-500/30 hover:bg-green-500/20 text-green-400 transition-colors"
                            >
                              <span className="text-lg">💬</span>
                              <span className="text-xs font-medium">WhatsApp</span>
                            </a>
                            <a
                              href={`https://t.me/share/url?url=${encodeURIComponent(inviteUrl)}&text=${encodeURIComponent(inviteText)}`}
                              target="_blank" rel="noopener noreferrer"
                              className="flex flex-col items-center gap-1 p-3 rounded-lg bg-blue-500/10 border border-blue-500/30 hover:bg-blue-500/20 text-blue-400 transition-colors"
                            >
                              <span className="text-lg">✈️</span>
                              <span className="text-xs font-medium">Telegram</span>
                            </a>
                            <a
                              href={`mailto:?subject=${encodeURIComponent(language === 'he' ? 'הצטרף ל-AI PhotoBooth' : 'Join AI PhotoBooth')}&body=${encodeURIComponent(inviteText)}`}
                              className="flex flex-col items-center gap-1 p-3 rounded-lg bg-slate-700/40 border border-slate-600 hover:bg-slate-700 text-slate-300 transition-colors"
                            >
                              <span className="text-lg">✉️</span>
                              <span className="text-xs font-medium">Email</span>
                            </a>
                            <button
                              onClick={async () => {
                                if (navigator.share) {
                                  try {
                                    await navigator.share({
                                      title: 'AI PhotoBooth',
                                      text: inviteText,
                                      url: inviteUrl,
                                    });
                                  } catch { /* user cancelled */ }
                                } else {
                                  navigator.clipboard.writeText(inviteText);
                                  toast.success(language === 'he' ? 'הטקסט הועתק לשיתוף' : 'Share text copied');
                                }
                              }}
                              className="flex flex-col items-center gap-1 p-3 rounded-lg bg-violet-500/10 border border-violet-500/30 hover:bg-violet-500/20 text-violet-400 transition-colors"
                            >
                              <span className="text-lg">📤</span>
                              <span className="text-xs font-medium">{language === 'he' ? 'אחר' : 'More'}</span>
                            </button>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}

                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-green-500/10 border border-green-500/20">
                    <Sparkles className="w-5 h-5 text-green-400" />
                    <span className="text-green-400 text-sm">
                      {language === 'he'
                        ? '+5 קרדיטים לך כשחבר קונה חבילה'
                        : '+5 credits for you when a friend purchases'}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-violet-500/10 border border-violet-500/20">
                    <Gift className="w-5 h-5 text-violet-400" />
                    <span className="text-violet-400 text-sm">
                      {language === 'he'
                        ? 'החבר מקבל 10 קרדיטים בונוס בהרשמה'
                        : 'Friend gets 10 bonus credits on signup'}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Quick Actions */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card className="glass-card border-slate-800 h-full">
              <CardHeader>
                <CardTitle className="text-white">
                  {language === 'he' ? 'פעולות מהירות' : 'Quick Actions'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Link to={createPageUrl('Photobooth')}>
                  <Button className="w-full justify-start bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-700 hover:to-pink-700 text-white h-14">
                    <Sparkles className="w-5 h-5 me-3" />
                    {language === 'he' ? 'צור תמונה חדשה' : 'Create New Image'}
                  </Button>
                </Link>

                <Link to={createPageUrl('Pricing')}>
                  <Button className="w-full justify-start bg-slate-700 hover:bg-slate-600 text-white h-14 font-medium">
                    <CreditCard className="w-5 h-5 me-3" />
                    {t('buyCredits')}
                  </Button>
                </Link>

                <Link to={createPageUrl('Gallery')}>
                  <Button className="w-full justify-start bg-slate-700 hover:bg-slate-600 text-white h-14 font-medium">
                    <Image className="w-5 h-5 me-3" />
                    {language === 'he' ? 'גלה דמויות חדשות' : 'Discover Characters'}
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Recent Generations */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-6"
        >
          <Card className="glass-card border-slate-800">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2">
                <Image className="w-5 h-5 text-violet-400" />
                {language === 'he' ? 'היסטוריית יצירות' : 'Generation History'}
              </CardTitle>
              <span className="text-xs text-slate-500">
                {generations.length} {language === 'he' ? 'תמונות' : 'images'}
              </span>
            </CardHeader>
            <CardContent>
              {loadingGens ? (
                <div className="text-center py-12 text-slate-500">{language === 'he' ? 'טוען...' : 'Loading...'}</div>
              ) : generations.length === 0 ? (
                <div className="text-center py-12">
                  <Image className="w-16 h-16 text-slate-700 mx-auto mb-4" />
                  <p className="text-slate-500 mb-4">
                    {language === 'he' ? 'עדיין לא יצרת תמונות. התחל עכשיו!' : 'No images created yet. Start now!'}
                  </p>
                  <Link to={createPageUrl('Photobooth')}>
                    <Button className="bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-700 hover:to-pink-700">
                      <Sparkles className="w-4 h-4 me-2" />
                      {language === 'he' ? 'צור את הראשונה שלך' : 'Create Your First'}
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {generations.map((g) => (
                    <button
                      key={g.id}
                      onClick={() => setViewing(g)}
                      className="relative aspect-square rounded-xl overflow-hidden bg-slate-800 border border-slate-700 hover:border-violet-500 transition group text-start"
                    >
                      <img
                        src={g.image_url}
                        alt={g.character_name || 'generation'}
                        className="w-full h-full object-cover group-hover:scale-105 transition"
                        loading="lazy"
                      />
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                        <p className="text-xs text-white font-medium truncate">{g.character_name || ''}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* My Custom Characters */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55 }}
          className="mt-6"
        >
          <Card className="glass-card border-slate-800">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2">
                <Wand2 className="w-5 h-5 text-pink-400" />
                {language === 'he' ? 'דמויות מותאמות אישית שיצרתי' : 'My Custom Characters'}
              </CardTitle>
              <Link to={createPageUrl('CustomCharacter')}>
                <Button size="sm" variant="outline" className="text-xs">
                  + {language === 'he' ? 'חדשה' : 'New'}
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {myCustomChars.length === 0 ? (
                <div className="text-center py-8 text-slate-500 text-sm">
                  {language === 'he' ? 'עדיין לא יצרת דמויות מותאמות אישית.' : 'You haven\'t created any custom characters yet.'}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                  {myCustomChars.map((c) => (
                    <div key={c.id} className="bg-slate-800/40 border border-slate-700 rounded-xl p-4 hover:border-pink-500/40 transition">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-pink-600 to-violet-600 flex items-center justify-center shrink-0">
                          <Wand2 className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-white font-semibold truncate">{c.name_he || c.name_en || c.name}</h3>
                          <p className="text-xs text-slate-400 line-clamp-2">{c.description || c.prompt?.slice(0, 80)}</p>
                        </div>
                      </div>
                      <Link
                        to={`${createPageUrl('Photobooth')}?customCharacterId=${c.id}`}
                        className="mt-3 inline-flex items-center gap-1 text-xs text-pink-400 hover:text-pink-300"
                      >
                        <Camera className="w-3 h-3" />
                        {language === 'he' ? 'השתמש' : 'Use'}
                      </Link>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* My Events */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mt-6"
        >
          <Card className="glass-card border-slate-800">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-400" />
                {language === 'he' ? 'האירועים שלי' : 'My Events'}
              </CardTitle>
              <Link to={createPageUrl('Events')}>
                <Button size="sm" variant="outline" className="text-xs">
                  + {language === 'he' ? 'אירוע חדש' : 'New Event'}
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {myEvents.length === 0 ? (
                <div className="text-center py-8 text-slate-500 text-sm">
                  {language === 'he' ? 'עדיין לא יצרת אירועים.' : 'You haven\'t created any events yet.'}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                  {myEvents.map((ev) => (
                    <div key={ev.id} className="bg-slate-800/40 border border-slate-700 rounded-xl p-4 hover:border-blue-500/40 transition">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-600 to-violet-600 flex items-center justify-center shrink-0">
                          <Frame className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-white font-semibold truncate">{ev.event_name || ev.name}</h3>
                          <p className="text-xs text-slate-400">
                            {ev.event_type} · {ev.allowed_characters?.length || 0} {language === 'he' ? 'דמויות' : 'chars'}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-3">
                        <button
                          onClick={() => { localStorage.setItem('base44_active_event_id', ev.id); navigate(createPageUrl('Photobooth')); }}
                          className="flex-1 inline-flex items-center justify-center gap-1 text-xs bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 py-1.5 rounded-lg"
                        >
                          <Camera className="w-3 h-3" />
                          {language === 'he' ? 'הפעל' : 'Activate'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Past photo — reprint, share or save it */}
      <AnimatePresence>
        {viewing && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setViewing(null)}
          >
            <motion.div
              initial={{ scale: 0.94, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.94, opacity: 0 }}
              className="bg-slate-900 border border-slate-800 rounded-2xl max-w-lg w-full overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
                <p className="text-white font-medium truncate">
                  {viewing.character_name || (language === 'he' ? 'תמונה' : 'Photo')}
                </p>
                <button onClick={() => setViewing(null)} className="text-slate-400 hover:text-white">
                  <XIcon className="w-5 h-5" />
                </button>
              </div>

              <img
                src={viewing.image_url}
                alt={viewing.character_name || ''}
                className="w-full max-h-[60vh] object-contain bg-black"
              />

              <div className="p-4 grid grid-cols-3 gap-2">
                <Button
                  onClick={() => { setPrinting(viewing.image_url); setViewing(null); }}
                  className="bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-700 hover:to-pink-700 text-white flex items-center justify-center gap-2"
                >
                  <Printer className="w-4 h-4" />
                  {language === 'he' ? 'הדפס' : 'Print'}
                </Button>

                <Button
                  variant="outline"
                  className="border-slate-700 text-slate-200 hover:bg-slate-800 flex items-center justify-center gap-2"
                  onClick={async () => {
                    try {
                      const res = await fetch(viewing.image_url);
                      const blob = await res.blob();
                      const file = new File([blob], 'photobooth.jpg', { type: blob.type || 'image/jpeg' });
                      if (navigator.canShare && navigator.canShare({ files: [file] })) {
                        await navigator.share({ files: [file], title: 'AI Photo Booth' });
                        return;
                      }
                      await navigator.clipboard.writeText(new URL(viewing.image_url, window.location.origin).href);
                      toast.success(language === 'he' ? 'הקישור הועתק' : 'Link copied');
                    } catch { /* the user dismissed the share sheet */ }
                  }}
                >
                  <Share2 className="w-4 h-4" />
                  {language === 'he' ? 'שתף' : 'Share'}
                </Button>

                <Button
                  variant="outline"
                  className="border-slate-700 text-slate-200 hover:bg-slate-800 flex items-center justify-center gap-2"
                  onClick={async () => {
                    try {
                      const res = await fetch(viewing.image_url);
                      const blob = await res.blob();
                      const href = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = href;
                      a.download = `photobooth-${viewing.character_name || viewing.id}.jpg`;
                      document.body.appendChild(a); a.click(); a.remove();
                      setTimeout(() => URL.revokeObjectURL(href), 10000);
                    } catch {
                      window.open(viewing.image_url, '_blank', 'noopener');
                    }
                  }}
                >
                  <Download className="w-4 h-4" />
                  {language === 'he' ? 'הורד' : 'Save'}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <PrintConfigModal
        isOpen={!!printing}
        onClose={() => setPrinting(null)}
        imageData={printing}
      />
    </div>
  );
}

